import { useMemo } from "react";
import { Link } from "react-router";
import { Headline } from "@/components/Headline";
import { ShortfallFixesState } from "@/generated/backend/v1/plan";
import type { Date as PDate } from "@/generated/core/v1/date";
import type { ShortfallFix } from "@/generated/core/v1/shortfall";
import { compareDates, formatDate, formatMonth } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import {
  isUnconfirmedFhsaTransfer,
  type Recommendation,
  shortfallView,
  type ShortfallView,
  sortedFixes,
  upcomingRecommendations,
} from "@/plan-logic/plan-view";
import { actionTitle, actionWhy, dueLabel, fhsaWarning } from "@/plan-logic/plan-view/actionText";
import { DataChecks } from "../dataChecks";
import { PlanPage, type PlanCtx } from "../PlanPage";
import { fixDetail, fixTitle } from "@/plan-logic/plan-view/fixText";

/** How far ahead the list looks: the plan's recommendations over its next three months. */
const MONTHS = 3;

/**
 * What a fix does to the shortfall, in two parts (the figure and its caption). The engine's
 * `closesAmount` is the whole shortfall when the fix moves it later, so a fix that only moves it
 * says where to, not "$X of the $X".
 */
function closesLabel(f: ShortfallFix, date: PDate, gap: number): [string, string] {
  if (!f.newShortfallDate) return ["All of it", "closed"];
  if (compareDates(f.newShortfallDate, date) > 0)
    return [formatMonth(f.newShortfallDate), "runs short then"];
  return [money(f.closesAmount), `of the ${money(gap)}`];
}

/** The shortfall fixes (FindShortfallFixes), or why there are none yet. */
function Fixes({ ctx, date, gap }: { ctx: PlanCtx; date: PDate; gap: number }) {
  const state = ctx.stored.shortfallFixesState;
  if (state === ShortfallFixesState.PENDING || state === ShortfallFixesState.UNSPECIFIED) {
    return (
      <div className="wp-pending" role="status" data-testid="fixes-pending">
        <div className="wp-spin" aria-hidden="true" />
        <span>
          Abi is re-running your plan with one thing changed at a time to find what closes it. This
          page fills in by itself when it&rsquo;s done.
        </span>
      </div>
    );
  }
  if (state === ShortfallFixesState.FAILED) {
    return (
      <p className="mut" data-testid="fixes-failed">
        We couldn&rsquo;t work out the fixes this time. They&rsquo;ll be tried again the next time
        your plan is rebuilt.
      </p>
    );
  }
  const fixes = sortedFixes(ctx.stored.shortfallFixes);
  if (!fixes.length) {
    return (
      <p className="mut" data-testid="fixes-none">
        None of the single changes Abi tried closes it on its own. Update your numbers on Update
        Data and Abi will look again.
      </p>
    );
  }
  return (
    <div data-testid="fixes">
      {fixes.map((f, i) => (
        <div className="fixrow" key={`${f.type}-${i}`}>
          <span className="n2">{i + 1}</span>
          <div style={{ flex: 1 }}>
            <b>{fixTitle(f)}</b>
            <p>
              {fixDetail(f)}
              {f.newShortfallDate
                ? ` On its own it moves the first short month to ${formatMonth(f.newShortfallDate)}.`
                : ""}
            </p>
          </div>
          <div className="cl">
            <b>{closesLabel(f, date, gap)[0]}</b>
            <span>{closesLabel(f, date, gap)[1]}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** "If your plan runs short" (what was the Runs Short page): when, how much, why, and the fixes. */
function Shortfall({ ctx, view }: { ctx: PlanCtx; view: ShortfallView }) {
  const d = view.details;
  const years = Math.floor(view.monthsAway / 12);
  return (
    <div className="card pad" id="short-card" data-testid="short-card">
      <h2 style={{ marginBottom: 4 }}>If your plan runs short</h2>
      <p className="mut" style={{ marginBottom: 14 }}>
        Abi runs your plan day by day to the end of your lives. This is what it found.
      </p>
      <div className="card pad" data-testid="shortfall">
        <div className="shortnum">
          <div>
            <span>When</span>
            <b>{formatMonth(view.date)}</b>
          </div>
          <div>
            <span>Short that day</span>
            <b className="tnum" style={{ color: "var(--warn)" }}>
              {money(view.amount)}
            </b>
          </div>
          <div>
            <span>Deepest point</span>
            <b className="tnum">{money(view.peak)}</b>
          </div>
          <div>
            <span>
              {years >= 1 ? "Years" : "Months"}{" "}
              {ctx.stale ? `after ${formatMonth(ctx.planStart)}` : "from now"}
            </span>
            <b>{years >= 1 ? years : view.monthsAway}</b>
          </div>
        </div>
        <h2 style={{ marginBottom: 6 }}>Why it happens</h2>
        <p style={{ color: "var(--ink2)", fontSize: 14, lineHeight: 1.6, maxWidth: "74ch" }}>
          {d
            ? `On ${formatDate(view.date)}, ${money(d.obligationsDue)} of bills and minimum payments is due, and there's ${money(d.chequingBalance)} in chequing. Everything Abi could have drawn on that day came to ${money(d.availableFunds)}, which leaves ${money(view.amount)} uncovered.`
            : `In ${formatMonth(view.date)} more is due than there is to pay it with.`}
        </p>
      </div>
      <div className="card pad" style={{ marginTop: 16 }}>
        <h2 style={{ marginBottom: 4 }}>What closes it</h2>
        <p className="mut" style={{ marginBottom: 10, maxWidth: "70ch" }}>
          Each of these is the plan re-run with one thing changed. You only need one. Each keeps
          your plan from running short at any point to its end, not only on that first day, so it
          can be much larger than the {money(view.amount)} short then.
        </p>
        <Fixes ctx={ctx} date={view.date} gap={view.amount} />
      </div>
      <p className="hint" style={{ maxWidth: "74ch" }}>
        A shortfall {years >= 3 ? "this far out " : ""}is a warning, not a verdict. It assumes you
        change nothing between now and then. Correcting your numbers on Update Data, or telling us
        what you actually did at a check-in, moves this date more than anything else.
      </p>
    </div>
  );
}

/** The shortfall at the head of the list (v42): rose, with a warning mark, open. */
function DangerRow({ view }: { view: ShortfallView }) {
  return (
    <details className="actrow danger" open data-testid="action-short">
      <summary>
        <span className="dangi" aria-hidden="true">
          !
        </span>
        <span style={{ flex: 1 }}>
          <b>Your plan runs short in {formatMonth(view.date)}</b>
          <span className="wy">This is the most important thing on the page.</span>
        </span>
        <span className="wt">{money(view.amount)} short</span>
      </summary>
      <div className="act-body">
        Abi runs your plan day by day and found a month where what is on hand does not cover what is
        due. The section underneath has the date, the size of it and what closes it.
        <div>
          <button
            type="button"
            className="mini"
            style={{ marginTop: 10 }}
            onClick={() =>
              document
                .getElementById("short-card")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Take me to it
          </button>
        </div>
      </div>
    </details>
  );
}

/** One recommended action: the title and when, opening to why it's on the list. */
function ActionRow({ r, ctx }: { r: Recommendation; ctx: PlanCtx }) {
  const f = r.first;
  const amount = r.occurrences > 1 ? r.total / r.occurrences : f.amount;
  const t = f.action.actionType;
  const unconfirmed = isUnconfirmedFhsaTransfer(f.action);
  const why = unconfirmed ? fhsaWarning(f, ctx.index) : actionWhy(f, ctx.index);
  return (
    <details className="actrow" data-testid={`action-${f.action.id}`}>
      <summary>
        <span style={{ flex: 1 }}>
          <b>
            {actionTitle(f, ctx.index, amount)}
            {r.occurrences > 1 ? `, ${r.occurrences} times` : ""}
          </b>
        </span>
        <span className="wt">{dueLabel(f.date, ctx.today)}</span>
      </summary>
      <div className="act-body">
        {why ? (
          <span className={unconfirmed ? "wy warn" : undefined}>{why}</span>
        ) : (
          "Part of the plan Abi built around your priorities."
        )}
        {t.oneofKind === "setGroupRrspContributionAction" && (
          <div style={{ marginTop: 10 }}>
            <Link
              className="mini"
              to={`/app/update-data?job=${t.setGroupRrspContributionAction.jobUuid}&rate=${t.setGroupRrspContributionAction.employeeContributionRate}`}
              data-testid="action-contribution"
            >
              Record it in Update Data &rarr;
            </Link>
          </div>
        )}
      </div>
    </details>
  );
}

function ActionsBody({ ctx }: { ctx: PlanCtx }) {
  const { abi, family, index } = ctx;
  const recs = useMemo(() => upcomingRecommendations(abi, { months: MONTHS }), [abi]);
  const short = shortfallView(abi);
  return (
    <>
      <Headline
        id="hd-act"
        cards={[
          {
            k: "Waiting on you",
            v: `${recs.length} ${recs.length === 1 ? "action" : "actions"}`,
            s: recs.length
              ? "over the next three months, in the order your plan does them"
              : "nothing waiting on you",
            testId: "act-waiting",
          },
          {
            k: "What they are worth",
            v: "Not worked out yet",
            pending: true,
            s: "Abi doesn’t put a dollar figure on each action yet, so there is no total to show.",
            testId: "act-worth",
          },
          {
            k: "Completed",
            v: "Not recorded yet",
            pending: true,
            s: "Your check-ins update the plan, but what you ticked off isn’t kept as a list yet.",
            testId: "act-completed",
          },
        ]}
      />
      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>Do these next</h2>
        <p className="mut" style={{ marginBottom: 14 }}>
          In the order your plan does them, soonest first.
        </p>
        <div id="act-full" data-testid="actions-list">
          {short && <DangerRow view={short} />}
          <DataChecks family={family} plan={abi} index={index} />
          {recs.length === 0 && (
            <p className="mut">Nothing to do right now beyond your usual bills.</p>
          )}
          {recs.map((r) => (
            <ActionRow
              key={`${r.first.kind}:${r.first.targetUuid}:${r.first.action.id}`}
              r={r}
              ctx={ctx}
            />
          ))}
        </div>
      </div>
      {short && <Shortfall ctx={ctx} view={short} />}
    </>
  );
}

/**
 * Actions (prototype v42/v45; it replaced Runs Short): the plan's recommended actions, each opening
 * to say why, with the "Check your data" cards pinned first and, when the plan runs short, the
 * shortfall at the head of the list and in full below it.
 */
export function ActionsPage() {
  return (
    <PlanPage title="pages.actions.title" sub="pages.actions.sub" testId="actions">
      {(ctx) => <ActionsBody ctx={ctx} />}
    </PlanPage>
  );
}
